import { useCallback, useEffect, useRef, useState } from "react";
import { Presence, Socket, type Channel } from "phoenix";
import { api } from "./api";

export type ObjectKind =
  | "camera"
  | "screen"
  | "image"
  | "note"
  | "backdrop"
  | "drawing";
export type Shape = "rect" | "rounded" | "circle";

export type RoomObject = {
  id: string;
  kind: ObjectKind;
  x: number;
  y: number;
  width: number;
  height: number;
  z: number;
  rotation: number;
  shape: Shape;
  data: Record<string, any>;
  subject_user_id: number | null;
  created_by_id: number | null;
};

export type PresentPerson = {
  user_id: number;
  display_name: string;
  identity: string;
};

export type Reaction = { id: string; emoji: string; display_name: string };

const API_URL = import.meta.env.VITE_API_URL ?? "";

function socketUrl(): string {
  if (API_URL) {
    return API_URL.replace(/^http/, "ws") + "/socket";
  }
  // Same-origin in development, through Vite's proxy.
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/socket`;
}

/**
 * Live room state: what is in the room, and who is in it.
 *
 * Dragging is split in two, matching the server. `moveObject` is a preview —
 * broadcast to everyone, written nowhere — and `placeObject` is the single
 * save when the drag ends. Writing every intermediate position would mean
 * hundreds of database writes per drag; broadcasting only at the end would
 * make objects teleport for everyone else.
 */
export function useRoomChannel(roomId: string | null) {
  const channelRef = useRef<Channel | null>(null);
  const socketRef = useRef<Socket | null>(null);

  const [objects, setObjects] = useState<Record<string, RoomObject>>({});
  const [present, setPresent] = useState<PresentPerson[]>([]);
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;

    (async () => {
      let token: string;
      try {
        ({ token } = await api.socketToken());
      } catch {
        if (!cancelled) setError("Couldn't open a live connection.");
        return;
      }
      if (cancelled) return;

      const socket = new Socket(socketUrl(), { params: { token } });
      socket.connect();
      socketRef.current = socket;

      const channel = socket.channel(`room:${roomId}`, {});
      channelRef.current = channel;

      channel.on("object:created", (obj: RoomObject) =>
        setObjects((o) => ({ ...o, [obj.id]: obj })),
      );

      channel.on("object:updated", (obj: RoomObject) =>
        setObjects((o) => ({ ...o, [obj.id]: obj })),
      );

      channel.on("object:deleted", ({ id }: { id: string }) =>
        setObjects((o) => {
          const next = { ...o };
          delete next[id];
          return next;
        }),
      );

      // Preview frames from someone else's in-progress drag. Applied to local
      // state but never saved — the authoritative position arrives with
      // object:updated when they let go.
      channel.on("object:moved", (patch: Partial<RoomObject> & { id: string }) =>
        setObjects((o) => {
          const existing = o[patch.id];
          if (!existing) return o;
          return {
            ...o,
            [patch.id]: {
              ...existing,
              x: patch.x ?? existing.x,
              y: patch.y ?? existing.y,
              width: patch.width ?? existing.width,
              height: patch.height ?? existing.height,
            },
          };
        }),
      );

      // Phoenix's Presence helper applies joins and leaves for us. The earlier
      // approach — pushing "presence_state" back at the server on every diff —
      // was asking for an event the server does not handle, so the list never
      // updated after the first snapshot.
      const presence = new Presence(channel);
      presence.onSync(() => {
        setPresent(
          presence.list((userId: string, { metas: [meta] }: any) => ({
            user_id: Number(userId),
            display_name: meta.display_name ?? "Guest",
            identity: meta.identity ?? `u_${userId}`,
          })),
        );
      });

      channel.on("reaction", ({ emoji, display_name }: any) => {
        const id = `${Date.now()}-${Math.random()}`;
        setReactions((r) => [...r, { id, emoji, display_name }]);
        window.setTimeout(
          () => setReactions((r) => r.filter((x) => x.id !== id)),
          2600,
        );
      });

      channel
        .join()
        .receive("ok", ({ objects }: { objects: RoomObject[] }) => {
          if (cancelled) return;
          setObjects(Object.fromEntries(objects.map((o) => [o.id, o])));
          setConnected(true);
        })
        .receive("error", () => {
          if (!cancelled) setError("Couldn't join this room.");
        });
    })();

    return () => {
      cancelled = true;
      channelRef.current?.leave();
      socketRef.current?.disconnect();
      channelRef.current = null;
      socketRef.current = null;
    };
  }, [roomId]);

  const moveObject = useCallback((patch: Partial<RoomObject> & { id: string }) => {
    // Local state updates immediately so the person dragging never feels lag,
    // regardless of the round trip.
    setObjects((o) => {
      const existing = o[patch.id];
      return existing ? { ...o, [patch.id]: { ...existing, ...patch } } : o;
    });
    channelRef.current?.push("object:move", patch);
  }, []);

  const placeObject = useCallback((patch: Partial<RoomObject> & { id: string }) => {
    // Applied locally first, like a move. At the end of a drag this changes
    // nothing — the preview already put it there — but for an edit that has
    // no preview, such as changing an object's layer or shape, this is the
    // difference between the button responding and appearing to do nothing
    // while the round trip happens.
    setObjects((o) => {
      const existing = o[patch.id];
      return existing ? { ...o, [patch.id]: { ...existing, ...patch } } : o;
    });
    channelRef.current?.push("object:place", patch);
  }, []);

  const createObject = useCallback((attrs: Partial<RoomObject>) => {
    channelRef.current?.push("object:create", attrs);
  }, []);

  const deleteObject = useCallback((id: string) => {
    channelRef.current?.push("object:delete", { id });
  }, []);

  const react = useCallback((emoji: string) => {
    channelRef.current?.push("react", { emoji });
  }, []);

  return {
    objects,
    present,
    reactions,
    connected,
    error,
    moveObject,
    placeObject,
    createObject,
    deleteObject,
    react,
  };
}
